import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import { SUPPORTED } from "@/i18n/translations";
import { SPECIES, spLabel } from "@/i18n/species";
import { tPlanName, tFeatures } from "@/i18n/plans_i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Logo, WordMark } from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import { FarmIcon } from "@/components/FarmIcon";
import SiteFooter from "@/components/SiteFooter";
import api from "@/lib/api";
import { track } from "@/lib/track";
import Seo from "@/components/Seo";
import { SEO_LANDING } from "@/i18n/seo_landing";
import { buildAlternates, localeUrl, localeOf } from "@/i18n/locales";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Bird, HeartPulse, ShoppingCart, Store, Users, Warehouse, BarChart3, Baby, Check, ArrowRight, Milk, Sparkles, MessagesSquare,
} from "lucide-react";

import { heroText } from "@/i18n/hero_i18n";
const ICONS = [Bird, Milk, Baby, Store, Users, Warehouse, BarChart3, HeartPulse];

// Optimized hero image (LCP): Pexels on-the-fly compression + sizing (~200KB vs 2.3MB).
const HERO_IMG = "/hero-mpf.webp";
const FEATURE_IMG = "https://images.pexels.com/photos/13575614/pexels-photo-13575614.jpeg?auto=compress&cs=tinysrgb&w=900";

export default function Landing() {
  const { lang, setLang, d } = useI18n();
  const params = useParams();
  const [prices, setPrices] = useState({});
  const [apiPlans, setApiPlans] = useState([]);

  useEffect(() => { track("landing_view"); }, []);

  // Preload the LCP hero image with high priority (biggest LCP win).
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = HERO_IMG;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  useEffect(() => {
    if (params.lang && SUPPORTED.includes(params.lang) && params.lang !== lang) setLang(params.lang);
  }, [params.lang]); // eslint-disable-line

  useEffect(() => {
    api.get("/subscription-plans").then(({ data }) => {
      const m = {};
      data.forEach((p) => { m[p.key] = p.price_monthly; });
      setPrices(m);
      setApiPlans([...data].sort((a, b) => (a.order || 0) - (b.order || 0)));
    }).catch(() => {});
  }, []);

  const prefix = SUPPORTED.includes(params.lang) && params.lang !== "fr" ? `/${params.lang}` : "";
  const marquee = [...SPECIES, ...SPECIES];

  // --- International SEO (localized meta + reciprocal hreflang + JSON-LD) ---
  const meta = SEO_LANDING[lang] || SEO_LANDING.fr;
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = localeUrl(lang, "/", siteUrl);
  const alternates = buildAlternates("/", siteUrl);
  const ogLocale = localeOf(lang).ogLocale;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Organization", name: "Ma Petite Ferme", url: siteUrl, logo: `${siteUrl}/logo512.png` },
    { "@context": "https://schema.org", "@type": "WebSite", name: "Ma Petite Ferme", url: siteUrl, inLanguage: localeOf(lang).locale },
    {
      "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Ma Petite Ferme",
      applicationCategory: "BusinessApplication", operatingSystem: "Web, iOS, Android",
      description: meta.description, offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    },
    ...(Array.isArray(d.faq) && d.faq.length ? [{
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: d.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A241E]">
      <Seo title={meta.title} description={meta.description} canonical={canonical}
           alternates={alternates} ogLocale={ogLocale} ogUrl={canonical}
           image={`${siteUrl}/logo512.png`} jsonLd={jsonLd} />
      {/* NAV */}
      <PublicHeader transparent />
      <main>

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt={heroText(lang).title} fetchPriority="high" loading="eager" decoding="async" width="1536" height="1024" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative max-w-6xl mx-auto px-5 pb-[156px] pt-32 w-full">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-head font-black text-[#16211B] text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.98] max-w-4xl">
            {heroText(lang).title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="text-lg sm:text-xl text-[#2E3D34] mt-6 max-w-xl leading-relaxed">
            {heroText(lang).subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }} className="flex flex-wrap gap-3 mt-[74px]">
            <Link to="/register" className="w-full sm:w-auto"><Button size="lg" className="w-full sm:w-auto min-w-[300px] justify-center rounded-2xl gap-3 h-16 text-lg font-semibold px-12 bg-[#D96C4A] hover:bg-[#BF583A] hover:scale-[1.03] transition-transform shadow-xl shadow-[#D96C4A]/40" data-testid="hero-cta-register"><FarmIcon className="h-10 w-10 shrink-0" /> {d.hero.cta1} <ArrowRight className="h-6 w-6 shrink-0" /></Button></Link>
            <Link to="/demo" className="w-full sm:w-auto"><Button size="lg" variant="outline" className="w-full sm:w-auto min-w-[260px] justify-center rounded-2xl gap-2 h-16 text-base px-10 bg-white/70 backdrop-blur-md text-[#2C4234] border-[#2C4234]/25 hover:bg-white" data-testid="hero-cta-demo"><Sparkles className="h-5 w-5" /> {d.hero.cta2 || "Essayer la démo"}</Button></Link>
            <Link to="/forum" className="w-full sm:w-auto"><Button size="lg" variant="ghost" className="w-full sm:w-auto justify-center rounded-2xl gap-2 h-16 text-base px-8 text-[#2C4234] hover:bg-white/60" data-testid="hero-cta-forum"><MessagesSquare className="h-5 w-5" /> Forum des éleveurs</Button></Link>
          </motion.div>
        </div>
      </section>

      {/* SPECIES MARQUEE */}
      <section className="py-8 bg-[#FAF7F2] border-y border-[#E5DEC4] overflow-hidden">
        <div className="marquee-mask">
          <div className="flex w-max animate-marquee gap-16">
            {marquee.map((s, i) => (
              <div key={i} className="flex items-center gap-3 shrink-0">
                <span className="text-3xl">{s.emoji}</span>
                <span className="font-head font-bold text-xl text-[#3A5644] whitespace-nowrap">{spLabel(lang, s.key)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES — BENTO */}
      <section id="fonctionnalites" className="py-24">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D96C4A] mb-3">{d.nav.features}</p>
          <h2 className="font-head font-extrabold text-3xl sm:text-5xl tracking-tight max-w-2xl">{d.featuresSection.title}</h2>
          <p className="text-[#3A5644] mt-4 text-lg max-w-2xl leading-relaxed">{d.featuresSection.subtitle}</p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {d.features.map((f, i) => {
              const Icon = ICONS[i] || Bird;
              const big = i === 0;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
                  className={big ? "md:col-span-2 md:row-span-1" : ""}>
                  <div className={`h-full rounded-3xl border border-[#E5DEC4] p-8 transition-transform hover:-translate-y-1 ${big ? "relative overflow-hidden text-white" : "bg-white"}`}>
                    {big && (
                      <>
                        <img src={FEATURE_IMG} alt={d.featuresSection.title} loading="lazy" decoding="async" width="1200" height="800" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#2C4234]/95 to-[#4E6E58]/70" />
                      </>
                    )}
                    <div className={`relative ${big ? "" : ""}`}>
                      <div className={`h-12 w-12 rounded-2xl grid place-items-center mb-5 ${big ? "bg-white/15 text-white backdrop-blur-md" : "bg-[#D96C4A]/10 text-[#D96C4A]"}`}><Icon className="h-6 w-6" /></div>
                      <h3 className={`font-head font-bold ${big ? "text-2xl" : "text-lg"}`}>{f.t}</h3>
                      <p className={`mt-2 leading-relaxed ${big ? "text-white/85 max-w-md" : "text-sm text-[#3A5644]"}`}>{f.d}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="tarifs" className="py-24 bg-white border-y border-[#E5DEC4]">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-head font-extrabold text-3xl sm:text-5xl tracking-tight text-center">{d.pricing.title}</h2>
          <p className="text-[#3A5644] mt-4 text-lg text-center">{d.pricing.subtitle}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {(apiPlans.length ? apiPlans : (d.plans || [])).map((ap) => {
              const name = tPlanName(lang, ap.name);
              const features = tFeatures(lang, ap.features || []);
              const featured = ap.key === "essential";
              const price = ap.price_monthly ?? prices[ap.key] ?? 0;
              return (
                <div key={ap.key} className={`rounded-3xl p-8 relative flex flex-col ${featured ? "bg-[#2C4234] text-white ring-2 ring-[#D96C4A] scale-[1.02]" : "bg-[#FAF7F2] border border-[#E5DEC4]"}`} data-testid={`plan-${ap.key}`}>
                  {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D96C4A] text-white text-xs font-bold">{d.pricing.popular}</span>}
                  <h3 className="font-head font-extrabold text-2xl">{name}</h3>
                  <p className="mt-4"><span className="font-head font-black text-5xl">{price}€</span><span className={featured ? "text-white/70" : "text-[#3A5644]"}>{ap.key === "free" ? "" : d.pricing.month}</span></p>
                  <ul className="mt-7 space-y-3 flex-1">
                    {features.map((it) => <li key={it} className="flex items-start gap-2.5 text-sm"><Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-[#F2B84B]" : "text-[#4E6E58]"}`} /> {it}</li>)}
                  </ul>
                  <Link to="/register" className="block mt-8"><Button className={`w-full rounded-full h-12 ${featured ? "bg-[#D96C4A] hover:bg-[#BF583A]" : "bg-transparent border border-[#4E6E58] text-[#2C4234] hover:bg-[#4E6E58] hover:text-white"}`} data-testid={`plan-choose-${ap.key}`}>{d.pricing.choose}</Button></Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="font-head font-extrabold text-3xl sm:text-5xl tracking-tight mb-10">{d.faqTitle}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {d.faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl px-6 border border-[#E5DEC4]">
                <AccordionTrigger className="text-left font-semibold font-head text-lg hover:text-[#D96C4A]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#3A5644] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5">
          <div className="relative rounded-[2rem] bg-[#2C4234] text-white p-12 sm:p-16 overflow-hidden">
            <div className="grain absolute inset-0 opacity-50" />
            <div className="relative">
              <h2 className="font-head font-black text-3xl sm:text-5xl tracking-tight max-w-2xl">{d.ctaSection.title}</h2>
              <p className="mt-4 text-white/80 text-lg max-w-xl leading-relaxed">{d.ctaSection.subtitle}</p>
              <Link to="/register"><Button size="lg" className="rounded-full mt-8 bg-[#D96C4A] hover:bg-[#BF583A] hover:scale-[1.03] transition-transform h-13 px-9 text-base" data-testid="cta-bottom-register">{d.ctaSection.button}</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      </main>
      <SiteFooter />
    </div>
  );
}
